import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getJobs } from './utils/jobApiCache';

// State abbreviation to full name mapping
const stateAbbreviations = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

// Function to extract state from title - looks for any 2 capital letters that match a state code
const extractStateFromTitle = (title) => {
  // Find all 2-letter uppercase sequences in the title
  const matches = title.match(/\b([A-Z]{2})\b/g);
  
  if (matches) {
    // Check each match to see if it's a valid state abbreviation
    for (const abbrev of matches) {
      if (stateAbbreviations[abbrev]) {
        return stateAbbreviations[abbrev];
      }
    }
  }
  return null;
};

// Function to transform HTML content - convert ol to ul and handle nested items
const transformHtmlContent = (html) => {
  if (!html) return html;
  
  // Remove Quill editor artifacts
  let transformed = html.replace(/<span class="ql-ui"[^>]*><\/span>/gi, '');
  
  // Convert <ol> to <ul> and </ol> to </ul>
  transformed = transformed.replace(/<ol\b[^>]*>/gi, '<ul>');
  transformed = transformed.replace(/<\/ol>/gi, '</ul>');
  
  // Process each list to handle ql-indent-1 nested items
  transformed = transformed.replace(/<ul>(.*?)<\/ul>/gis, (match, listContent) => {
    // Extract all list items
    const items = [];
    const itemRegex = /<li([^>]*)>(.*?)<\/li>/gi;
    let itemMatch;
    
    while ((itemMatch = itemRegex.exec(listContent)) !== null) {
      items.push({
        attributes: itemMatch[1],
        content: itemMatch[2],
        isIndented: itemMatch[1].includes('ql-indent-1')
      });
    }
    
    // Build result with proper nesting
    let result = '<ul>';
    let i = 0;
    
    while (i < items.length) {
      const item = items[i];
      
      if (item.isIndented) {
        // This shouldn't happen at the start, but handle it
        i++;
        continue;
      }
      
      // Regular item - check if it has nested items following it
      const cleanAttrs = item.attributes
        .replace(/\s*data-list="[^"]*"/gi, '')
        .trim();
      result += `<li${cleanAttrs ? ' ' + cleanAttrs : ''}>${item.content}`;
      
      // Check for consecutive indented items
      let nestedItems = [];
      let j = i + 1;
      while (j < items.length && items[j].isIndented) {
        nestedItems.push(items[j]);
        j++;
      }
      
      if (nestedItems.length > 0) {
        // Add nested list
        result += '<ul>';
        for (let k = 0; k < nestedItems.length; k++) {
          const nestedItem = nestedItems[k];
          const nestedAttrs = nestedItem.attributes
            .replace(/\s*class="[^"]*ql-indent-1[^"]*"/gi, '')
            .replace(/\s*data-list="[^"]*"/gi, '')
            .trim();
          result += `<li${nestedAttrs ? ' ' + nestedAttrs : ''}>${nestedItem.content}</li>`;
        }
        result += '</ul>';
        i = j; // Skip processed nested items
      } else {
        i++;
      }
      
      result += '</li>';
    }
    
    result += '</ul>';
    return result;
  });
  
  // Final cleanup - remove any remaining ql-indent classes and data-list attributes
  transformed = transformed.replace(/\s*class="[^"]*ql-indent[^"]*"/gi, '');
  transformed = transformed.replace(/\s*data-list="[^"]*"/gi, '');
  
  return transformed;
};

// Function to transform API job data
const transformJobData = (apiJob) => {
  let state = null;
  let city = null;
  let country = "United States";
  
  // First priority: Check job_listing_state and job_listing_city
  if (apiJob.job_listing_state) {
    state = apiJob.job_listing_state;
  }
  if (apiJob.job_listing_city) {
    city = apiJob.job_listing_city;
  }
  
  // If state/city not found, try to extract state code from title
  if (!state) {
    state = extractStateFromTitle(apiJob.title);
  }
  
  // If title extraction didn't work, use job_listing_region
  if (!state && apiJob.job_listing_region) {
    if (apiJob.job_listing_region !== "United States") {
      // Use region as state (e.g., "Alaska", "Virginia", etc.)
      state = apiJob.job_listing_region;
    }
  }
  
  if (!state) {
    state = "United States";
  }

  let jobType = apiJob.job_listing_type || "Full Time";
  if (jobType.toLowerCase().includes("part")) {
    jobType = "Part Time";
  } else if (jobType.toLowerCase().includes("full")) {
    jobType = "Full Time";
  } else if (jobType.toLowerCase().includes("contract")) {
    jobType = "Contract";
  } else if (jobType.toLowerCase().includes("intern")) {
    jobType = "Internship";
  }

  let category = apiJob.job_listing_category || "Operations";
  category = category.replace(/\s*-\s*CRLLC\s*$/, "").trim();

  let applyUrl = apiJob.apply_link;
  if ( apiJob.zoho_job_id) {
    applyUrl = `https://hrms.credentia.com/login?redirect-to=/job-form/new?job_title=${apiJob.zoho_job_id}#login`;
  }
  // If apply_link is an email, convert to mailto link
  if (applyUrl && applyUrl.includes('@') && !applyUrl.startsWith('mailto:') && !applyUrl.startsWith('http')) {
    applyUrl = `mailto:${applyUrl}`;
  }
  if (!applyUrl) {
    applyUrl = "#";
  }

  // Transform HTML content to convert ol to ul
  const transformedContent = transformHtmlContent(apiJob.content);

  return {
    id: apiJob.id,
    title: apiJob.title,
    slug: apiJob.slug,
    company: apiJob.job_listing_category || "Operations - CRLLC",
    location: {
      country: country,
      state: state,
      city: city
    },
    jobType: jobType,
    category: category,
    applyUrl: applyUrl,
    content: transformedContent,
    zoho_job_id: apiJob.zoho_job_id
  };
};

function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch job data (with caching)
  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const apiData = await getJobs();
        
        if (apiData && Array.isArray(apiData)) {
          const foundJob = apiData.find(j => j.slug === slug);
          if (foundJob) {
            setJob(transformJobData(foundJob));
          } else {
            setError('Job not found');
          }
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (err) {
        console.error('Error fetching job:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchJob();
    }
  }, [slug]);

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Loading job details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-white p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Job Not Found</h1>
            <p className="text-gray-600 mb-6">{error || "The job you're looking for doesn't exist."}</p>
            <Link
              to="/"
              className="inline-block bg-primary-blue text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
            >
              Back to Careers
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-2 sm:p-6 md:p-8">
      <div className="max-w-4xl mx-auto text-left px-4">
        {/* Back Icon */}
        <Link
          to="/"
          className="inline-flex items-center text-dark-blue hover:opacity-80 mb-4 sm:mb-6 transition-opacity"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="font-medium text-sm sm:text-base">Back</span>
        </Link>

        {/* Category */}
        <div className="mb-2 text-left">
          <span className="text-gray-400 text-xs sm:text-sm">{job.category}</span>
        </div>

        {/* Title and Tags */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark-blue flex-1 text-left">
            {job.title}
          </h1>
          <div className="flex gap-2 flex-shrink-0">
            <span className=" text-dark-blue border-[1px] border-dark-blue px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium">
              {job.jobType}
            </span>
            {job.remote && (
              <span className="text-dark-blue border-[1px] border-dark-blue px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium">
                Remote
              </span>
            )}
          </div>
        </div>

        {/* Company Card with Logo and Apply Button */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mb-6 sm:mb-8 flex flex-col sm:flex-row items-center sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center p-2 flex-shrink-0">
              <img 
                src={`${process.env.PUBLIC_URL}/icon.svg`}
                alt="Credentia Icon" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="text-xs sm:text-sm text-gray-500">
                {job.location.city && job.location.state && job.location.state !== "United States"
                  ? `${job.location.state} - ${job.location.city}`
                  : job.location.state && job.location.state !== "United States"
                  ? job.location.state
                  : job.location.country || 'N/A'}
              </div>
            </div>
          </div>
          <a
            href={job.applyUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary-blue text-white px-4 sm:px-6 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity inline-block w-full sm:w-auto text-center text-sm sm:text-base"
          >
            Apply for job
          </a>
        </div>

        {/* Job Description */}
        {job.content ? (
          <div 
            className="space-y-6 prose prose-lg max-w-none job-content"
            dangerouslySetInnerHTML={{ __html: job.content }}
          />
        ) : null}
      </div>
    </div>
  );
}

export default JobDetail;

